// Ficha do produto no canal Mercado Livre (plano mapeamento-campos F5/F7): tipos da resposta da API
// (product.ml_ficha) e funcoes puras da tela. Sem React, para testar com node:test.

export type MlOrigin = "override" | "base" | "sisplan" | "category" | "channel" | "none";

export interface MlFichaField {
  key: string;
  label: string;
  type: "text" | "select" | "number";
  options?: string[];
  value: string;
  origin: MlOrigin;
  origin_label: string;
  override: string | null;
}

export interface MlFicha {
  title: { value: string; length: number; max: number; source: string; piece_type: string | null };
  brand: { value: string; origin: string; origin_label: string };
  composition: { value: string; origin: "override" | "base" | "none"; base: string; override: string | null };
  category: { internal_id: number; ml_category_id: string; name: string } | null;
  fields: MlFichaField[];
  free_shipping_from: number;
}

/** Valor escolhido para um atributo da categoria: id da lista do ML (lista fechada) ou texto livre. */
export interface AttrChoice {
  value_id?: string;
  value_name?: string;
}

export interface ProductAttribute {
  id: string;
  name: string;
  required: boolean;
  conditional: boolean;
  values: Array<{ id: string; name: string }>;
  category_value: AttrChoice | null;
  override: AttrChoice | null;
}

/** Nome legivel do valor da categoria (a API devolve id; a tela mostra o nome da lista). */
export function categoryValueLabel(a: ProductAttribute): string {
  const v = a.category_value;
  if (!v) return "";
  if (v.value_id) return a.values.find((x) => x.id === v.value_id)?.name ?? v.value_id;
  return v.value_name ?? "";
}

/** Chaves de field_overrides que a ficha ML edita (o resto do JSON e preservado). */
export const FICHA_OVERRIDE_KEYS = [
  "ml_model",
  "ml_gender",
  "ml_age_group",
  "ml_listing_type",
  "ml_pkg_height_cm",
  "ml_pkg_width_cm",
  "ml_pkg_length_cm",
  "ml_pkg_weight_g",
  "ml_composition",
] as const;

const NUMERIC = new Set(["ml_pkg_height_cm", "ml_pkg_width_cm", "ml_pkg_length_cm", "ml_pkg_weight_g"]);

/** field_overrides novo: o que a ficha nao conhece fica como esta; campo desligado ou vazio sai (volta a herdar). */
export function fichaPatch(
  current: Record<string, unknown>,
  draft: Record<string, string>,
  on: Record<string, boolean>,
  /** Atributos escolhidos no produto ({id: valor}); omitido = nao mexe em ml_attrs. */
  attrs?: Record<string, AttrChoice>,
): Record<string, unknown> {
  const out: Record<string, unknown> = { ...current };
  if (attrs !== undefined) {
    const chosen = Object.fromEntries(Object.entries(attrs).filter(([, v]) => v.value_id || v.value_name));
    if (Object.keys(chosen).length > 0) out.ml_attrs = chosen;
    else delete out.ml_attrs;
  }
  for (const k of FICHA_OVERRIDE_KEYS) {
    const v = (draft[k] ?? "").trim();
    if (on[k] === true && v !== "") out[k] = NUMERIC.has(k) ? Number(v) : v;
    else delete out[k];
  }
  return out;
}

export function titleCounter(length: number, max: number): { text: string; over: boolean } {
  return { text: `${length}/${max}`, over: length > max };
}

/** Frete gratis: o ML obriga a partir de um valor (regra do ML, so leitura). */
export function freeShippingText(price: number, from: number): string {
  return price >= from
    ? `Frete grátis obrigatório (o Mercado Livre exige a partir de R$ ${from}).`
    : `Abaixo de R$ ${from} o frete grátis é opção da loja.`;
}

/** Cor da etiqueta de origem: so o que muda o significado (override e Sisplan); o resto fica neutro. */
export function originTone(origin: MlOrigin): { bg: string; fg: string } | null {
  if (origin === "override") return { bg: "var(--color-coral-soft)", fg: "var(--color-coral)" };
  if (origin === "sisplan") return { bg: "var(--color-soft)", fg: "var(--color-ink-2)" };
  return null;
}
